import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import core from '@bravissimolabs/website-core';
import { scroller } from 'react-scroll';
import { cloneDeep } from 'lodash';

import Meta from 'shared/containers/MetaContainer';
import { openLayer, closeLayer } from 'shared/actions/layer';
import Results from 'brastrap/promotional/results/Results';
import { replaceAttribute } from 'brastrap/promotional/shop-by-container/ShopByContainer';
import combine from './ProductListingPageContainer/combine';

import {
  receiveProductListing,
  apiProductListingRequest,
  selectStyleColour,
  clearScroll,
} from '../../../actions/product-listing';
import {
  buildQuery,
  getFacetsFromQuery,
  isSizeSelector,
  markSelected,
  paramToKey,
  getGroupedSizeFacets,
} from '../../../utils/product-listing';
import { saveItem } from '../../../actions/saved-items';
import { BROWSER_ID_STORAGE_KEY } from '../../../constants/auth';
import { sendToSli } from '../actions';

import { sizeFacetData as initialSizeFacetData } from '../../../utils/sizeSelectorData';
import { sizeFacetData as initialUSSizeFacetData } from '../../../utils/usSizeSelectorData';
import {
  dataLayerGA4ViewItemList,
  dataLayerNavItemSelected,
} from '../../../utils/data-layer';

const LISTING_CONTAINER_ID = 'listing-container';
const RESULTS_CONTAINER_ID = 'listing-results';

const SKIN_TONE = 'skin-tone';

/**
 * Format sizes to add size mapping for range sizes, ex. D-E
 * @param {string} size
 * @param {bool} isCupSizeUrl
 * @return {object|null}
 */
const getSizeFacetsFromShopBySize = (size, isCupSizeUrl = false) => {
  const braSizeRegExp = isCupSizeUrl
    ? /([0-9]{1,2})?([d-lD-L]{1,2})/
    : /([0-9]{1,2})([d-lD-L]{1,2})/;
  const braSizeMatch = size.match(braSizeRegExp);
  if (braSizeMatch && braSizeMatch.length >= 3) {
    const [, backSize, cupSize] = braSizeMatch;
    const result = { cupsize: [cupSize.toLowerCase()] };
    if (backSize) {
      result.backsize = [backSize];
    }
    return result;
  }
  return null;
};

/**
 * Create query param from ShopBy data
 * @param {object} shopByCollectionData
 * @return {object}
 */
const createShopByQueryParams = shopByCollectionData =>
  shopByCollectionData &&
  shopByCollectionData.attribute &&
  shopByCollectionData.attribute !== 'size'
    ? { [`f_${shopByCollectionData.attribute}`]: shopByCollectionData.value }
    : {};

/**
 * I do very much dislike this.
 * I have run into a big bug keeping react-router instate with our redux state.
 * So updating the url with the query params after retrieving the product keeps the client in sync with the data
 * stored in the redux state.
 * However clicking the back button through the browser does not rewind history in the state.
 * So I have had to bind to the onpopstate in the window to ensure the products displayed are what the customer is
 * expecting.
 *
 * The "Official" answer from Dan "The Man" Redux is to use the query params as the main data store for this information
 * and not to include it in the state. However this will not work for us as we could potentially be returning 1000s of
 * styleColours and requesting them all just to filter on the frontend seems like a massive overhead.
 *
 * Further reading:
 * - http://stackoverflow.com/a/36749963/1499016
 * - https://github.com/ReactTraining/react-router/issues/967
 */
class ProductListingPage extends Component {
  /**
   * Constructor
   * @param {object} props
   */
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  /**
   * Send data to ga4 analytics
   */
  componentWillMount() {
    dataLayerGA4ViewItemList(
      this.props.items,
      this.props.collection,
      this.props.location.pathname
    );
  }

  /**
   * Bind to the onpopstate of the window and route to our callback
   */
  componentDidMount() {
    window.onpopstate = () => this.onBrowserNavigationPress(); // eslint-disable-line no-undef
    if (typeof window !== 'undefined') {
      localStorage.setItem('Collection', this.props.collection);
    }

    if (
      typeof window !== 'undefined' &&
      window.klaviyo &&
      this.props?.collection &&
      this.props?.pageTitle
    ) {
      const url = `${window.location.host}${window.location.pathname}`;
      const imageUrl =
        this.props?.containers?.[0]?.rows?.[0]?.items?.[0]?.image?.src || null;

      const categoryItem = {
        Title: this.props.pageTitle,
        CategoryID: this.props.collection
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        URL: url,
        ImageURL: `https:${imageUrl}`,
      };
      window.klaviyo.track('Viewed Category', categoryItem);
    }
  }

  /**
   * Scroll to top if fetching and the window has been scrolled below this element.
   */
  componentDidUpdate() {
    const { scrollTo } = this.props;
    const el =
      scrollTo === LISTING_CONTAINER_ID &&
      this.el.getBoundingClientRect().top < 0
        ? LISTING_CONTAINER_ID
        : scrollTo;
    if (!this.props.isFetching && scrollTo && el) {
      scroller.scrollTo(el, { duration: 500, smooth: true });
      this.props.dispatch(clearScroll());
    }

    const storedItem = window.localStorage.getItem('SelectedNavItem');
    if (storedItem !== this.ref.current) {
      dataLayerNavItemSelected(window.localStorage.getItem('SelectedNavItem'));
    }
    this.ref.current = storedItem;
  }

  /**
   * Check the current query is up to date with the state.
   * If not then dispatch the api call to receive the correct products.
   */
  onBrowserNavigationPress() {
    const {
      collection,
      dispatch,
      filter: { subcollection, limit, sortBy },
      location: { query },
      pagination: { page },
      params,
      searchPhrase,
      facets,
      isShopByCollection,
      shopByCollectionData,
      defaults,
    } = this.props;

    const filters = { subcollection, limit, page, sortBy };
    const queryParams = {
      limit: `${defaults.limit}`,
      page: `${defaults.page}`,
      sortBy: defaults.sortBy,
      ...query,
    };

    // If the params driven by react router, are not the same as the ones stored in state then we need to update.
    // Also check the url params are in sync with the state - if not, then force the page to retrieve the correct
    // products from the server.
    let updateState =
      (params.collection && params.collection !== collection) ||
      (params.searchPhrase && params.searchPhrase !== searchPhrase) ||
      Object.keys(filters).some(
        key => `${queryParams[key]}` !== `${filters[key]}`
      );

    // Build the query with the params passed through from the url - we'll use this to compare to state and see if
    // any facets have changed.
    const allFacets = getFacetsFromQuery(query);

    if (!updateState) {
      const stateFacets = facets.reduce((acc, val) => {
        val.values
          .filter(v => v.selected)
          .forEach(v => {
            acc.push({ [`${val.name.toLowerCase()}`]: v.id });
          });
        return acc;
      }, []);
      updateState =
        JSON.stringify(stateFacets).length !== JSON.stringify(allFacets).length;
    }

    if (updateState) {
      const queryWithoutSizeFacets = {};
      let sizeFacets = getGroupedSizeFacets(allFacets.filter(isSizeSelector));

      Object.keys(query).forEach(param => {
        if (!isSizeSelector({ [paramToKey(param)]: query[param] })) {
          queryWithoutSizeFacets[param] = query[param];
        }
      });

      let newCollection = params.collection;
      if (isShopByCollection) {
        const valueArray = params.value.split('-');
        const [value, ...slug] = valueArray; // eslint-disable-line no-unused-vars
        newCollection = `shop-by-${params.attribute}-${
          slug.length ? slug.join('-') : params.value
        }`;
        const isCupSizeUrl = slug.length && slug.indexOf('cup') !== -1;
        sizeFacets = getSizeFacetsFromShopBySize(value, isCupSizeUrl);
      }

      dispatch(
        apiProductListingRequest(
          newCollection,
          params.searchPhrase,
          sizeFacets,
          {
            ...buildQuery(queryWithoutSizeFacets, { facets: [], sizeFacets }),
            ...createShopByQueryParams(shopByCollectionData),
          },
          {
            updateUrl: false,
          }
        )
      );
    }
  }
  /**
   * Inserts content modules from Contentful into products arrays
   * on collections pages in the position specified by Contentful
   * @returns {Object}
   */
  getPropsWithCombinedItemsAndContent = () => {
    const { contentModules, items } = this.props;
    if (!contentModules || !contentModules.length) {
      return this.props;
    }

    return {
      ...this.props,
      items: combine(items, contentModules),
      collection: this.props.collection,
    };
  };

  /**
   * @return {XML}
   */
  render() {
    const {
      doNotIndex,
      onSaveItemPress,
      pagination: { page },
      pageDescription,
      pageTitle,
      savedItemsMap,
      searchPhrase,
    } = this.props;

    return (
      <Meta
        title={
          !searchPhrase && page > 1 ? `${pageTitle} | Page ${page}` : pageTitle
        }
        description={pageDescription}
        pageType="productListing"
        doNotIndex={doNotIndex || searchPhrase}
      >
        <div
          id={LISTING_CONTAINER_ID}
          ref={element => {
            this.el = element;
          }}
        >
          <Results
            {...this.getPropsWithCombinedItemsAndContent()}
            savedItemsMap={savedItemsMap}
            onSaveItemPress={onSaveItemPress}
            resultsContainerId={RESULTS_CONTAINER_ID}
            collection={this.props.collection}
            pathname={this.props.location.pathname}
          />
        </div>
      </Meta>
    );
  }
}

ProductListingPage.propTypes = {
  ...Results.propTypes,
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  doNotIndex: PropTypes.bool,
  onStyleColourChange: PropTypes.func,
  modalId: PropTypes.string,
  isOpen: PropTypes.bool,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
  onSaveItemPress: PropTypes.func,
  savedItemsMap: PropTypes.object,
};

/**
 * - Retrieve & dispatch the products.
 * - Set the correct page number.
 * - Calculate what increments to show based on the total number of products.
 *
 * @param {Object} store
 * @param {Object} props
 * @param {Object} req
 * @param {Object} res
 */
ProductListingPage.populateStore = async (store, props, req, res) => {
  let { collection } = props.params;
  const { searchPhrase } = props.params;
  let decodedSearchPhrase;
  if (searchPhrase) {
    decodedSearchPhrase = decodeURI(searchPhrase);
  }
  let { query } = req;
  const {
    query: { subcollection, limit, page, sortBy },
  } = req;

  const browserId =
    req.cookies[BROWSER_ID_STORAGE_KEY] ||
    req.body.browserId ||
    req.params.browserId;
  const ipAddress = req.ip;
  const referringPage = `${req.protocol}://${req.get('host')}${req.baseUrl}${
    req.path
  }`;

  const userAgent = req.headers['user-agent'];
  let sizeFacets = getFacetsFromQuery(query).filter(isSizeSelector);
  sizeFacets = getGroupedSizeFacets(sizeFacets);

  const isShopByCollection = !collection && req.path.includes('shop-by');
  const colourIsSkinTone =
    isShopByCollection &&
    props.params.value &&
    props.params.value.includes(SKIN_TONE);
  const shopByCollectionData = {};
  if (isShopByCollection) {
    shopByCollectionData.attribute = props.params.attribute;
    const valueArray = colourIsSkinTone
      ? [
          `${SKIN_TONE.replace('-', '')}s`,
          ...props.params.value.replace(`${SKIN_TONE}-`, '').split('-'),
        ]
      : props.params.value.split('-');
    const [value, ...slug] = valueArray;
    shopByCollectionData.value = value;
    collection = `shop-by-${props.params.attribute}-${
      slug.length ? slug.join('-') : value
    }`;

    // Using size facets if this is shop-by-size to allow for mapping to sizes with ranges ex. D-E
    if (shopByCollectionData.attribute === 'size') {
      // Check if the url is shop by cup or not
      const isCupSizeUrl = slug.length && slug.indexOf('cup') !== -1;
      sizeFacets = getSizeFacetsFromShopBySize(
        shopByCollectionData.value,
        isCupSizeUrl
      );
    } else {
      query = { ...query, ...createShopByQueryParams(shopByCollectionData) };
    }
  }

  const productListing = await core.product.product.getProductListing({
    collection,
    subcollection,
    facets: getFacetsFromQuery(buildQuery(query, { sizeFacets })).filter(
      f => !isSizeSelector(f)
    ),
    ipAddress,
    limit,
    page: page ? parseInt(page, 10) : 1,
    searchPhrase: decodedSearchPhrase,
    sortBy,
    referringPage,
    userAgent,
    userId: browserId || store.getState().app.config.serverId,
    learning: !!+req.query.learning,
    tunings: !!+req.query.tunings,
    metadata: !!+req.query.metadata,
    slug: req.path,
  });

  if (productListing.aliasRedirectUrl) {
    // Redirect the url to the actual slug url if current collection slug is alias
    // and return status code 301
    res.status(301);
    res.locals.redirectUrl = `${productListing.aliasRedirectUrl}`;
  } else {
    if (
      productListing.isInvalidCollection ||
      productListing.items?.length === 0
    ) {
      // Return a 404 status code if the collection is invalid or if there are no items to display
      res.status(404);
    }
    if (isShopByCollection) {
      shopByCollectionData.value = colourIsSkinTone
        ? SKIN_TONE.replace('-', ' ')
        : shopByCollectionData.value;

      const { attribute, value } = shopByCollectionData;
      productListing.pageTitle = replaceAttribute(
        productListing.pageTitle,
        attribute,
        value,
        attribute === 'size'
      );
      productListing.pageDescription = replaceAttribute(
        productListing.pageDescription,
        attribute,
        value
      );
      productListing.containers = (productListing.containers || []).map(c => ({
        ...c,
        ...shopByCollectionData,
        modifiers: ['shop-by'],
      }));
    }

    store.dispatch(
      receiveProductListing({
        ...productListing,
        sizeFacets,
        isShopByCollection,
        shopByCollectionData,
      })
    );
  }
};

/**
 * @param {Object} stateProps
 * @param {Object} dispatchProps
 * @param {Object} ownProps
 * @return {*}
 */
const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const {
    collection,
    facets,
    sizeFacets,
    filter: { subcollection, limit, sortBy },
    pagination: { page },
    searchPhrase,
    shopByCollectionData,
    outlet,
  } = stateProps;
  const { dispatch } = dispatchProps;
  const queryParams = { limit, page, sortBy };
  const { params } = ownProps.match;
  const sizeFacetData =
    outlet === 'US'
      ? markSelected(cloneDeep(initialUSSizeFacetData), sizeFacets)
      : markSelected(cloneDeep(initialSizeFacetData), sizeFacets);

  // Avoid a missing subcollection getting treated as "undefined"
  if (subcollection) {
    queryParams.subcollection = subcollection;
  }

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,

    params,
    sizeFacetData,

    /**
     * Triggered on a change of filters. Update the query params with the new values.
     *
     * @param {Object} filters
     * @return {*}
     */
    onFilter: filters => {
      let scrollTo;
      if (Object.keys(filters).indexOf('subcollection') >= 0) {
        // No default as need to be able to deselect
        if (filters.subcollection) {
          queryParams.subcollection = filters.subcollection;
          scrollTo = RESULTS_CONTAINER_ID;
        } else {
          delete queryParams.subcollection;
        }
      }
      queryParams.limit = filters.limit || limit;
      queryParams.sortBy = filters.sortBy || sortBy;
      const query = {
        ...buildQuery(queryParams, { facets, sizeFacets }),
        ...createShopByQueryParams(shopByCollectionData),
      };

      return dispatch(
        apiProductListingRequest(collection, searchPhrase, sizeFacets, query, {
          scrollTo,
        })
      );
    },

    /**
     * Add or remove the facet and build the query string to send to the server.
     * As updating the facets will mess up the pagination, make sure we request the first page.
     *
     * @param {boolean} isSelected
     * @param {string} valueId
     * @param {string} facetId
     * @return {*}
     */
    onFacetChange: (isSelected, valueId, facetId) => {
      queryParams.page = 1;
      const newFacet = { isSelected, valueId, facetId };
      const query = {
        ...buildQuery(queryParams, { facets, sizeFacets }, newFacet),
        ...createShopByQueryParams(shopByCollectionData),
      };
      return dispatch(
        apiProductListingRequest(
          collection,
          searchPhrase,
          sizeFacets,
          query,
          {}
        )
      );
    },

    /**
     * Build a new query string for queries built by the size selector. Handles
     * selecting size facets.
     *
     * @param {array} newFacets
     * @return {*}
     */
    onReceiveSizeFacets: newFacets => {
      queryParams.page = 1;
      const query = {
        ...buildQuery(
          queryParams,
          { facets, sizeFacets: newFacets },
          null,
          true
        ),
        ...createShopByQueryParams(shopByCollectionData),
      };
      return dispatch(
        apiProductListingRequest(collection, searchPhrase, newFacets, query, {})
      );
    },

    /**
     * Triggered when the user tries to paginate through the pages.
     * Update the query params with the requested page.
     *
     * @param {number} requestedPage
     * @return {*}
     */
    onPaginate: requestedPage => {
      queryParams.page = requestedPage;
      const query = {
        ...buildQuery(queryParams, { facets, sizeFacets }),
        ...createShopByQueryParams(shopByCollectionData),
      };
      return dispatch(
        apiProductListingRequest(collection, searchPhrase, sizeFacets, query, {
          scrollTo: RESULTS_CONTAINER_ID,
        })
      );
    },

    // Ping sli with the style colour and the ranking.
    // This does not need to be dispatched as it will not alter state.
    onStyleColourClick: styleCode => sendToSli(styleCode, stateProps),

    onSaveItemPress: item => dispatch(saveItem(item)),
  };
};

const modalId = 'product-listing-page.modal';

/**
 * @param {number} maxItems
 * @param {String} mediaType
 * @return {number}
 */
const calculateMaxPages = (maxItems, mediaType) => {
  const pagesToDeviceMap = {
    extraSmall: 2,
    small: 3,
    medium: 5,
    large: 7,
    xlarge: 10,
  };
  return pagesToDeviceMap[mediaType] || maxItems;
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const mapStateToProps = state => ({
  ...state.productListing,
  modalId,
  isOpen: state.layer.target === modalId && state.layer.isVisible,
  currency: state.app.config.currency,
  outlet: state.app.config.outlet,
  showFacetsInModal: state.browser.deviceIs.mobile,
  pagination: {
    ...state.productListing.pagination,
    maxItems: calculateMaxPages(
      state.productListing.pagination.maxItems,
      state.browser.mediaType
    ),
  },
  savedItemsMap: state.savedItems.itemMap,
  locale: state.app.locale.selected,
});

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
const mapDispatchToProps = dispatch => ({
  dispatch,
  onStyleColourChange: styleColourCode =>
    dispatch(selectStyleColour(styleColourCode)),
  onModalOpen: target => dispatch(openLayer(target)),
  onModalClose: () => dispatch(closeLayer()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(ProductListingPage)
);
